import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	getState,
	organizerListBarsApi,
	organizerGetBarApi,
	organizerCreateBarApi,
	organizerUpdateBarApi,
	organizerDeleteBarApi,
	organizerToggleBarApi,
} from "../../api/api";
import dayjs from "dayjs";
import { Row, Col } from "react-bootstrap";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { handleError } from "../../helpers/handleError";
import { OrganizerHeaderContext } from "../Layout";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import OnlineBookingSwitch from "../components/OnlineBookingSwitch";
import useCitiesByState from "../../hooks/useCitiesByState";
import OrganizerTimePillSelect from "../components/OrganizerTimePillSelect";
import "../dashboard.css";
import "./reservas-organizer.css";
import "../events/EventForm.css";
import { MAIN_API } from "../../api/apilist";
import { COUNTRIES } from "../../pages/reservas/components/ReservaShared";
import { calculateBarCapacity as calculateBarTotalCapacity } from "../../pages/reservas/components/barCapacity";
import ClubAddressAutocomplete from "../canchas/ClubAddressAutocomplete";
import ClubLocationPicker from "../canchas/ClubLocationPicker";
import { buildBarFormData } from "./barFormData";
import RecordatoriosAjustes from "../recordatorios/RecordatoriosAjustes";
import PedidoResenaAjustes from "../recordatorios/PedidoResenaAjustes";
import ListaEsperaAjustes from "../recordatorios/ListaEsperaAjustes";
import { useVolverAPoliticas } from "../recordatorios/volverAPoliticas";
import { AvisoBorrador, borrarBorrador, useBorradorPoliticas } from "../recordatorios/borradorPoliticas";
import { faltaListaEspera, faltaResena, faltaWhatsapp, recordatoriosDeVenue } from "../recordatorios/recordatorios";
import ReservasGuidedFlow from "./primerUso/ReservasGuidedFlow";
import useMultiSede from "../hooks/useMultiSede";
import OrgReservationsScreen from "./components/OrgReservationsScreen";
import AvisoSenasBloqueadas from "../components/AvisoSenasBloqueadas";
import useCobrosHabilitados from "../hooks/useCobrosHabilitados";
import { sinSenasEnBar } from "../../helpers/senasOrganizer";

const resolveUrl = (u) => {
	if (!u) return "";
	if (/^https?:\/\//i.test(u)) return u;
	if (/^blob:/i.test(u)) return u;
	if (u.startsWith("/")) return `${MAIN_API}${u}`;
	return `${MAIN_API}/${u}`;
};

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const FALLBACK_IMAGE = "https://via.placeholder.com/400x130?text=Bar";

const WEEK_DAYS = [
	{ weekday: 0, label: "Domingo" },
	{ weekday: 1, label: "Lunes" },
	{ weekday: 2, label: "Martes" },
	{ weekday: 3, label: "Miércoles" },
	{ weekday: 4, label: "Jueves" },
	{ weekday: 5, label: "Viernes" },
	{ weekday: 6, label: "Sábado" },
];

const SECTION_IDS = [
	"info",
	"ubicacion",
	"horarios",
	"distribucion",
	"politicas",
	"pago",
];
const SECTION_LABELS = [
	"INFORMACIÓN GENERAL",
	"UBICACIÓN",
	"HORARIO",
	"DISTRIBUCIÓN",
	"POLÍTICAS",
	"PAGOS",
];

const MIN_ADVANCE_OPTIONS = [
	{ value: 30, label: "30 min" },
	{ value: 60, label: "1 hora" },
	{ value: 120, label: "2 horas" },
	{ value: 240, label: "4 horas" },
	{ value: 480, label: "8 horas" },
	{ value: 1440, label: "1 día" },
];

const SLOT_DURATION_OPTIONS = [
	{ value: 60, label: "60 min" },
	{ value: 90, label: "90 min" },
	{ value: 120, label: "2 horas" },
	{ value: 150, label: "2:30 hs" },
	{ value: 180, label: "3 horas" },
	{ value: 240, label: "4 horas" },
];

const CANCELLATION_HOURS_OPTIONS = [
	{ value: 1, label: "1 hora antes" },
	{ value: 2, label: "2 horas antes" },
	{ value: 6, label: "6 horas antes" },
	{ value: 12, label: "12 horas antes" },
	{ value: 24, label: "24 horas antes" },
	{ value: 48, label: "48 horas antes" },
];

const reservationWindowOptions = Array.from(
	{ length: 30 },
	(_, i) => i + 1,
).map((d) => ({
	value: d,
	label: `${d} ${d === 1 ? "día" : "días"}`,
}));

const toleranceOptions = [5, 10, 15, 20, 30].map((v) => ({
	value: v,
	label: `${v} min`,
}));

/* ─────────────────────────────────────────
   HELPERS (hoisted above component)
───────────────────────────────────────── */
function getCleanErrorMessage(error) {
	if (!error) return "";
	if (error.response?.data) {
		const data = error.response.data;
		if (typeof data === "string") return data;
		if (data.message) {
			let msg = data.message;
			if (data.errors) {
				if (Array.isArray(data.errors)) {
					msg +=
						"\n" +
						data.errors
							.map((e) =>
								typeof e === "string" ? e : e.message || JSON.stringify(e),
							)
							.join("\n");
				} else if (typeof data.errors === "object") {
					msg +=
						"\n" +
						Object.entries(data.errors)
							.map(([k, v]) => `- ${k}: ${v}`)
							.join("\n");
				}
			}
			return msg;
		}
	}
	return error.message || "Ocurrió un error inesperado. Intenta nuevamente.";
}

function calculateHours(open, close) {
	if (!open || !close) return 0;
	const [openH, openM] = open.split(":").map(Number);
	const [closeH, closeM] = close.split(":").map(Number);
	let openMin = openH * 60 + openM;
	let closeMin = closeH * 60 + closeM;
	if (closeMin <= openMin) {
		closeMin += 24 * 60;
	}
	return (closeMin - openMin) / 60;
}

function defaultOpeningHours() {
	return WEEK_DAYS.map((d) => ({
		weekday: d.weekday,
		enabled: d.weekday >= 4 && d.weekday <= 6,
		open: "20:00",
		close: "02:00",
	}));
}

function defaultZones() {
	return [
		{
			name: "Interior",
			tableCount: 4,
			tableCapacity: 4,
			price: 0,
			priceEnabled: false,
		},
	];
}

function normalizeBarCoordinate(value) {
	if (value === "" || value == null) return "";
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : "";
}

export { calculateBarTotalCapacity };

export function applyManualBarAddressEdit(barForm, address) {
	return { ...barForm, address, lat: "", lng: "" };
}

export function emptyBarForm() {
	const zones = defaultZones();
	return {
		barId: "",
		name: "",
		slug: "",
		shortDescription: "",
		description: "",
		address: "",
		lat: "",
		lng: "",
		cityid: "",
		stateid: "",
		// New bars start with online booking off (backend default matches); legacy bars with no value show ON on edit.
		onlineBookingEnabled: false,
		reservationMode: "free",
		depositAmount: 0,
		slotDurationMinutes: 120,
		reservationWindowDays: 6,
		maxPartySize: 10,
		cancellationPolicyHours: 24,
		isFeatured: false,
		amenitiesText: "",
		openingHours: defaultOpeningHours(),
		zones,
		zonePricingEnabled: false,
		totalCapacity: calculateBarTotalCapacity(zones),
		contactPhone: "",
		contactEmail: "",
		minAdvanceMinutes: 120,
		cancellationAllowed: true,
		toleranceMinutes: 15,
		...recordatoriosDeVenue(),
		additionalNotes: "",
		imageFile: null,
		existingImage: "",
		instagram: "",
		website: "",
		existingGallery: [],
		galleryFiles: [],
	};
}

export function normalizeBarForForm(bar) {
	if (!bar) return emptyBarForm();
	const opening = Array.isArray(bar.openingHours) ? bar.openingHours : [];
	const openingMap = new Map(opening.map((row) => [Number(row.weekday), row]));
	const rawZones =
		Array.isArray(bar.zones) && bar.zones.length ? bar.zones : defaultZones();
	const zones = rawZones.map((z) => ({
		name: z.name || "",
		tableCount: Number(z.tableCount || 1),
		tableCapacity: Number(z.tableCapacity || 4),
		price: Number(z.price || 0),
		priceEnabled: Boolean(z.priceEnabled),
	}));
	return {
		barId: bar._id || "",
		name: bar.name || "",
		slug: bar.slug || "",
		shortDescription: bar.shortDescription || "",
		description: bar.description || "",
		address: bar.address || "",
		lat: normalizeBarCoordinate(bar.lat),
		lng: normalizeBarCoordinate(bar.lng),
		cityid: bar.cityid?._id || bar.cityid || "",
		stateid: bar.stateid?._id || bar.stateid || "",
		// Legacy bars have no value and count as enabled; only an explicit false blocks public booking.
		onlineBookingEnabled: bar.onlineBookingEnabled !== false,
		reservationMode: bar.reservationMode || "free",
		depositAmount: Number(bar.depositAmount || 0),
		slotDurationMinutes: Number(bar.slotDurationMinutes || 120),
		reservationWindowDays: Number(bar.reservationWindowDays || 6),
		maxPartySize: Number(bar.maxPartySize || 10),
		cancellationPolicyHours: Number(bar.cancellationPolicyHours || 24),
		isFeatured: Boolean(bar.isFeatured),
		amenitiesText: Array.isArray(bar.amenities) ? bar.amenities.join(", ") : "",
		openingHours: WEEK_DAYS.map((d) => {
			const source = openingMap.get(d.weekday);
			return {
				weekday: d.weekday,
				enabled: source ? Boolean(source.enabled) : false,
				open: source?.open || "20:00",
				close: source?.close || "02:00",
			};
		}),
		zones,
		zonePricingEnabled: Boolean(bar.zonePricingEnabled),
		totalCapacity: calculateBarTotalCapacity(zones),
		contactPhone: bar.contactPhone || "",
		contactEmail: bar.contactEmail || "",
		minAdvanceMinutes: Number(bar.minAdvanceMinutes || 120),
		cancellationAllowed: bar.cancellationAllowed !== false,
		toleranceMinutes: Number(bar.toleranceMinutes || 15),
		...recordatoriosDeVenue(bar.reminders),
		additionalNotes: bar.additionalNotes || "",
		imageFile: null,
		existingImage: bar.imageUrl || bar.image || bar.mobileimage || "",
		instagram: bar.instagram || "",
		website: bar.website || "",
		existingGallery: Array.isArray(bar.galleryimage) ? bar.galleryimage : [],
		galleryFiles: [],
	};
}

export { buildBarFormData };

/* ─────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────── */

/* Toast notification */
function OrgToast({ message, onClose }) {
	useEffect(() => {
		if (!message) return;
		const t = setTimeout(onClose, 3000);
		return () => clearTimeout(t);
	}, [message, onClose]);

	if (!message) return null;
	return (
		<div className="org-toast" role="alert">
			{message}
		</div>
	);
}

/* Confirm modal */
function ConfirmModal({ barName, onConfirm, onCancel }) {
	return (
		<div className="zr-overlay" onMouseDown={onCancel}>
			<div className="zr-modal" onMouseDown={(e) => e.stopPropagation()}>
				<div className="zr-modal__icon">
					<svg
						width="56"
						height="56"
						viewBox="0 0 24 24"
						fill="currentColor"
						style={{ color: "var(--zr-lime)" }}
					>
						<path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 5.5a1.5 1.5 0 0 1 3 0v6a1.5 1.5 0 0 1-3 0zM12 18.4a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z" />
					</svg>
				</div>
				<h3 className="zr-modal__title">¿Eliminar bar?</h3>
				<p className="zr-modal__text">
					Se eliminará <strong>{barName}</strong>. Esta acción no se puede
					deshacer.
				</p>
				<div className="zr-modal__actions">
					<button
						type="button"
						className="zr-btn zr-btn--ghost"
						onClick={onCancel}
					>
						Cancelar
					</button>
					<button
						type="button"
						className="zr-btn zr-btn--dark"
						style={{ minWidth: 160, height: 46 }}
						onClick={onConfirm}
					>
						Eliminar
					</button>
				</div>
			</div>
		</div>
	);
}

/* ─────────────────────────────────────────
   BarCardItem (helper for OrgListScreen)
───────────────────────────────────────── */
function BarCardItem({
	bar,
	onEdit,
	onDelete,
	onToggleStatus,
	onShowReservations,
	onCopyPublicLink,
	getCityName,
	getStateName,
}) {
	const [menuOpen, setMenuOpen] = React.useState(false);
	const menuRef = React.useRef(null);

	React.useEffect(() => {
		if (!menuOpen) return undefined;
		const close = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target))
				setMenuOpen(false);
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, [menuOpen]);

	const city = getCityName(bar);
	const state = getStateName(bar);
	const locationText = [bar.address, city, state].filter(Boolean).join(" • ");
	const isActive = bar.isEnabled !== false;
	const totalTables =
		Array.isArray(bar.zones) && bar.zones.length > 0
			? bar.zones.reduce((sum, zone) => sum + Number(zone.tableCount || 0), 0)
			: (bar.tablesCount ?? 0);
	const coverUrl = bar.imageUrl
		? resolveUrl(bar.imageUrl)
		: bar.image
			? resolveUrl(bar.image)
			: FALLBACK_IMAGE;

	return (
		<Col xs={12} md={6} xl={4}>
			<div className="zr-barcard" style={menuOpen ? { zIndex: 10 } : {}}>
				<div
					className="zr-barcard__cover"
					style={{ backgroundImage: `url(${coverUrl})` }}
				>
					<span
						className={`zr-barcard__status ${isActive ? "zr-barcard__status--active" : "zr-barcard__status--inactive"}`}
					>
						<span className="dot" />
						{isActive ? "Activo" : "Inactivo"}
					</span>
				</div>
				<div className="zr-barcard__body">
					<div className="zr-barcard__titlerow">
						<h3 className="zr-barcard__name">{bar.name}</h3>
						<div ref={menuRef} style={{ position: "relative" }}>
							<button
								type="button"
								className="zr-barcard__menu"
								onClick={() => setMenuOpen((v) => !v)}
								aria-label="Acciones"
							>
								•••
							</button>
							{menuOpen && (
								<div className="zr-cardmenu">
									<button
										type="button"
										onClick={() => {
											setMenuOpen(false);
											onEdit(bar._id);
										}}
									>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M16.5 4.5 19.5 7.5 8 19l-3.5.5L5 16z" />
											<path d="M14.5 6.5 17.5 9.5" />
										</svg>
										Editar
									</button>
									<button
										type="button"
										onClick={() => {
											setMenuOpen(false);
											onDelete(bar);
										}}
									>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
										</svg>
										Eliminar
									</button>
									<button
										type="button"
										onClick={() => {
											setMenuOpen(false);
											onCopyPublicLink(bar);
										}}
									>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
											<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
										</svg>
										Copiar enlace
									</button>
									<button
										type="button"
										onClick={() => {
											setMenuOpen(false);
											onToggleStatus(bar);
										}}
									>
										<svg
											width="14"
											height="14"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<circle cx="12" cy="12" r="10" />
											<line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
										</svg>
										{isActive ? "Desactivar" : "Activar"}
									</button>
								</div>
							)}
						</div>
					</div>
					{locationText && (
						<p className="zr-barcard__addr">
							<svg
								width="14"
								height="17"
								viewBox="0 0 24 24"
								fill="currentColor"
								stroke="none"
							>
								<path d="M12 2C8.1 2 5 5.1 5 9c0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
							</svg>
							{locationText}
						</p>
					)}
					<div className="zr-barcard__stats">
						<span className="zr-stat">
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="currentColor"
							>
								<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
							</svg>
							<span className="zr-stat__txt">
								<b>{String(calculateBarTotalCapacity(bar.zones)).padStart(2, "0")}</b>
								<span>Cap. Máx.</span>
							</span>
						</span>
						<span className="zr-stat">
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="currentColor"
							>
								<rect x="8" y="4" width="8" height="16" rx="2" />
								<rect x="4" y="6" width="2" height="4" rx="1" />
								<rect x="4" y="14" width="2" height="4" rx="1" />
								<rect x="18" y="6" width="2" height="4" rx="1" />
								<rect x="18" y="14" width="2" height="4" rx="1" />
							</svg>
							<span className="zr-stat__txt">
								<b>{String(totalTables).padStart(2, "0")}</b>
								<span>Mesas</span>
							</span>
						</span>
						<span className="zr-stat">
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M3 20L7 6h11l3 14H10z" />
								<path d="M7 6l3 14" />
								<path d="M12 10h5" />
								<path d="M13 13h5" />
								<path d="M14 16h5" />
							</svg>
							<span className="zr-stat__txt">
								<span>
									<b>{String(bar.reservationsCount ?? 0).padStart(2, "0")}</b>{" "}
									Reservas
								</span>
								<span>Próximas</span>
							</span>
						</span>
					</div>
					<div className="zr-barcard__footer">
						<button
							type="button"
							className="zr-btn zr-btn--lime zr-btn--block"
							onClick={() => onShowReservations(bar)}
						>
							<svg
								width="15"
								height="15"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
								<line x1="16" y1="2" x2="16" y2="6" />
								<line x1="8" y1="2" x2="8" y2="6" />
								<line x1="3" y1="10" x2="21" y2="10" />
								<path d="m9 16 2 2 4-4" />
							</svg>
							Ver Reservas
						</button>
					</div>
				</div>
			</div>
		</Col>
	);
}

/* ─────────────────────────────────────────
   OrgListScreen
───────────────────────────────────────── */
function OrgListScreen({
	bars,
	states,
	loading,
	multiSede,
	onEmpezar,
	onEdit,
	onDelete,
	onToggleStatus,
	onShowReservations,
	onCopyPublicLink,
}) {
	const [search, setSearch] = useState("");

	const filtered = bars.filter(
		(b) =>
			b.status !== false &&
			b.name?.toLowerCase().includes(search.toLowerCase()),
	);

	// The organizer API populates cityid with its name; the (per-province) form list is
	// not loaded on this screen, so never depend on a full city catalog here.
	const getCityName = (bar) => bar.cityid?.name || "";
	const getStateName = (bar) =>
		states.find(
			(s) => String(s._id) === String(bar.stateid?._id || bar.stateid),
		)?.name || "";

	return (
		<div>
			<div className="zr-list-toolbar">
				<label className="zr-search">
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<circle cx="11" cy="11" r="7" />
						<path d="m20 20-3.2-3.2" />
					</svg>
					<input
						type="text"
						placeholder="Buscar por nombre"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</label>
				{/* Solo los negocios multi-sede crean otro bar; el resto tiene uno (ver README del back). */}
				{multiSede && bars.length > 0 && (
					<button type="button" className="zr-btn zr-btn--dark" onClick={onEmpezar}>
						Crear bar
					</button>
				)}
			</div>

			{loading && <SpinLoading />}

			{!loading && bars.length === 0 && (
				<div className="zr-panel" style={{ padding: 0 }}>
					<div className="zr-empty">
						<p>Todavía no configuraste tus reservas de mesas.</p>
						<button type="button" className="zr-btn zr-btn--dark" onClick={onEmpezar}>
							Empezar configuración
						</button>
					</div>
				</div>
			)}

			{!loading && bars.length > 0 && filtered.length === 0 && (
				<div className="zr-panel" style={{ padding: 0 }}>
					<div className="zr-empty">No hay bares registrados.</div>
				</div>
			)}

			{!loading && filtered.length > 0 && (
				<Row className="zr-bars-grid">
					{filtered.map((bar) => (
						<BarCardItem
							key={bar._id}
							bar={bar}
							onEdit={onEdit}
							onDelete={onDelete}
							onToggleStatus={onToggleStatus}
							onShowReservations={onShowReservations}
							onCopyPublicLink={onCopyPublicLink}
							getCityName={getCityName}
							getStateName={getStateName}
						/>
					))}
				</Row>
			)}
		</div>
	);
}

/* ─────────────────────────────────────────
   OrgReservationsScreen (extracted, Unit 7 / PR7, task 7.2) —
   src/organizer/reservas/components/OrgReservationsScreen.jsx.
   Imported above; shared with CanchasOrganizer.jsx (variant="club").
───────────────────────────────────────── */

/* ─────────────────────────────────────────
   OrgFormScreen
───────────────────────────────────────── */
function OrgFormScreen({
	barForm,
	setBarForm,
	states,
	saving,
	onSave,
	onCancel,
	formError,
	senasBloqueadas,
	initialSection = 0,
}) {
	// Sin cobros validados el bar se muestra (y se guarda) gratis aunque traiga una seña vieja.
	const cobraSenas = !senasBloqueadas && barForm.reservationMode === "deposit";
	// City options depend on the selected province (never the full national catalog).
	const { cities } = useCitiesByState(barForm.stateid);
	const [activeSection, setActiveSection] = useState(initialSection);
	const sectionRefs = useRef([]);
	const fileInputRef = useRef(null);
	const [dragTarget, setDragTarget] = useState("");

	const [selectedCountry, setSelectedCountry] = useState(() => {
		const phone = barForm.contactPhone || "";
		for (const c of COUNTRIES) {
			if (phone.startsWith(c.prefix)) {
				return c;
			}
		}
		return COUNTRIES[0];
	});

	const [phoneVal, setPhoneVal] = useState(() => {
		const phone = barForm.contactPhone || "";
		for (const c of COUNTRIES) {
			if (phone.startsWith(c.prefix)) {
				return phone.slice(c.prefix.length).trim();
			}
		}
		return phone.trim();
	});

	const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const countryRef = useRef(null);

	const [phoneError, setPhoneError] = useState("");
	const [waError, setWaError] = useState("");
	const [resenaError, setResenaError] = useState("");
	const [listaEsperaError, setListaEsperaError] = useState("");
	const [phoneTouched, setPhoneTouched] = useState(false);

	useEffect(() => {
		if (phoneTouched) {
			const cleanNum = phoneVal.trim();
			if (!cleanNum) {
				setPhoneError("El teléfono de contacto es obligatorio.");
			} else if (!selectedCountry.validate(phoneVal)) {
				setPhoneError("Este número no es válido");
			} else {
				setPhoneError("");
			}
		} else {
			setPhoneError("");
		}
	}, [phoneVal, selectedCountry, phoneTouched]);

	const handleSaveClick = () => {
		setPhoneTouched(true);
		const cleanNum = phoneVal.trim();
		let err = "";
		if (!cleanNum) {
			err = "El teléfono de contacto es obligatorio.";
		} else if (!selectedCountry.validate(phoneVal)) {
			err = "Este número no es válido";
		}
		setPhoneError(err);
		const faltaWa = faltaWhatsapp(barForm.whatsapp);
		setWaError(faltaWa);
		const faltaResenaWa = faltaResena(barForm.resena);
		setResenaError(faltaResenaWa);
		const faltaListaWa = faltaListaEspera(barForm.listaEspera);
		setListaEsperaError(faltaListaWa);
		if (faltaWa || faltaResenaWa || faltaListaWa) return;
		onSave();
	};

	useEffect(() => {
		const handleDocClick = (e) => {
			if (countryRef.current && !countryRef.current.contains(e.target)) {
				setCountryDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleDocClick);
		return () => document.removeEventListener("mousedown", handleDocClick);
	}, []);

	useEffect(() => {
		const phone = barForm.contactPhone || "";
		let matchedCountry = COUNTRIES[0];
		let matchedNumber = phone.trim();
		for (const c of COUNTRIES) {
			if (phone.startsWith(c.prefix)) {
				matchedCountry = c;
				matchedNumber = phone.slice(c.prefix.length).trim();
				break;
			}
		}
		setSelectedCountry(matchedCountry);
		setPhoneVal(matchedNumber);
		setPhoneTouched(false);
		setPhoneError("");
	}, [barForm.barId]);

	const handlePhoneChange = (val, country = selectedCountry) => {
		setPhoneVal(val);
		const cleanNum = val.trim();
		if (cleanNum) {
			setBarForm((prev) => ({
				...prev,
				contactPhone: `${country.prefix} ${cleanNum}`,
			}));
		} else {
			setBarForm((prev) => ({
				...prev,
				contactPhone: "",
			}));
		}
	};

	const handleCountryChange = (c) => {
		setSelectedCountry(c);
		setCountryDropdownOpen(false);
		const cleanNum = phoneVal.trim();
		if (cleanNum) {
			setBarForm((prev) => ({
				...prev,
				contactPhone: `${c.prefix} ${cleanNum}`,
			}));
		} else {
			setBarForm((prev) => ({
				...prev,
				contactPhone: "",
			}));
		}
	};

	const createDropHandlers = (key, onFile) => ({
		onDragEnter: (e) => {
			e.preventDefault();
			e.stopPropagation();
			setDragTarget(key);
		},
		onDragOver: (e) => {
			e.preventDefault();
			e.stopPropagation();
			setDragTarget(key);
		},
		onDragLeave: (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (e.currentTarget.contains(e.relatedTarget)) return;
			setDragTarget((prev) => (prev === key ? "" : prev));
		},
		onDrop: (e) => {
			e.preventDefault();
			e.stopPropagation();
			setDragTarget((prev) => (prev === key ? "" : prev));
			const file = e.dataTransfer?.files?.[0] || null;
			if (file) onFile(file);
		},
	});

	const handleClearCover = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setBarForm((prev) => ({ ...prev, imageFile: null, existingImage: "" }));
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const setField = (field, value) =>
		setBarForm((prev) => ({ ...prev, [field]: value }));

	// Edits made before leaving for Zoco Chat to manage WhatsApp templates come back unsaved.
	const borrador = useBorradorPoliticas({
		id: barForm.barId,
		form: barForm,
		restaurar: (datos) => setBarForm((prev) => ({ ...prev, ...datos })),
	});

	const setOpeningHour = (weekday, key, value) =>
		setBarForm((prev) => ({
			...prev,
			openingHours: prev.openingHours.map((row) =>
				Number(row.weekday) === Number(weekday)
					? { ...row, [key]: value }
					: row,
			),
		}));

	const scrollToSection = (idx) => {
		setActiveSection(idx);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const renderFormFooter = () => (
		<div className="zr-formfooter">
			<span className="zr-formfooter__status">
				<span className="dot" />
				<span className="main-status">
					{barForm.barId
						? `Editando ${barForm.name || "bar"}`
						: "Creando nuevo bar"}
				</span>
				<span className="sub-status">
					{" "}
					• {barForm.zones.reduce((s, z) => s + Number(z.tableCount || 0), 0)}{" "}
					mesas •{" "}
					{barForm.zones.reduce(
						(s, z) =>
							s + Number(z.tableCount || 0) * Number(z.tableCapacity || 4),
						0,
					)}{" "}
					asientos
				</span>
			</span>
			<div className="zr-formfooter__actions">
				<button
					type="button"
					className="zr-btn zr-btn--ghost"
					onClick={onCancel}
					disabled={saving}
				>
					Cancelar
				</button>
				<button
					type="button"
					className="zr-btn zr-btn--dark"
					onClick={handleSaveClick}
					disabled={saving}
				>
					{saving ? "Guardando..." : "Guardar Cambios"}
				</button>
			</div>
		</div>
	);

	const handleImageChange = (e) => {
		const file = e.target.files?.[0];
		if (file) setField("imageFile", file);
	};

	return (
		<div style={{ paddingBottom: "60px" }}>
			<AvisoBorrador visible={borrador.recuperado} />
			{formError && (
				<div
					style={{
						background: "#fdecea",
						border: "1px solid #f8b4b4",
						borderRadius: "10px",
						padding: "14px 18px",
						marginBottom: "24px",
						color: "#e53935",
						fontSize: "13px",
						fontWeight: "600",
						display: "flex",
						alignItems: "flex-start",
						gap: "10px",
					}}
				>
					<span style={{ fontSize: "16px", lineHeight: "1" }}>⚠️</span>
					<div style={{ whiteSpace: "pre-line", flex: 1 }}>
						<strong style={{ display: "block", marginBottom: "4px" }}>
							No pudimos guardar los cambios:
						</strong>
						{formError}
					</div>
				</div>
			)}

			{/* Tab navigation */}
			<nav className="zr-tabs">
				{SECTION_IDS.map((id, idx) => (
					<button
						key={id}
						type="button"
						className={
							"zr-tabs__item" +
							(activeSection === idx ? " zr-tabs__item--active" : "")
						}
						onClick={() => scrollToSection(idx)}
					>
						{SECTION_LABELS[idx]}
					</button>
				))}
			</nav>

			{/* Sections */}
			<div style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
				{/* Section 1: Información */}
				{activeSection === 0 && (
					<section id="sec-info">
						<div className="zr-panel">
							<div className="zr-section-head">
								<h2 className="zr-section-head__title">Información del bar</h2>
								<p className="zr-section-head__hint">
									| Los datos principales que verán tus clientes.
								</p>
							</div>
							<Row className="g-4">
								<Col md={6} className="d-flex flex-column gap-4">
									<div className="zr-field">
										<span className="zr-field__label">Nombre del bar</span>
										<input
											className="zr-input"
											value={barForm.name}
											onChange={(e) => setField("name", e.target.value)}
											placeholder="Ej: Nórdico Rooftop"
										/>
									</div>
									<div className="zr-field">
										<span className="zr-field__label">Descripción</span>
										<textarea
											className="zr-textarea"
											style={{ height: 220, minHeight: 220 }}
											value={barForm.description}
											onChange={(e) => setField("description", e.target.value)}
											placeholder="Contá más sobre el bar..."
										/>
									</div>
								</Col>
								<Col md={6} className="d-flex flex-column gap-4">
									<div className="zr-field">
										<span className="zr-field__label">Nro de celular</span>
										<div
											ref={countryRef}
											style={{
												display: "flex",
												alignItems: "center",
												border: phoneError
													? "1px solid #e53935"
													: "1px solid var(--zr-ink)",
												borderRadius: "var(--zr-radius)",
												height: "40px",
												background: "#fff",
												position: "relative",
												boxSizing: "border-box",
												width: "100%",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: "8px",
													paddingLeft: "14px",
													paddingRight: "12px",
													height: "100%",
													background: "#f9fafb",
													borderTopLeftRadius: "9px",
													borderBottomLeftRadius: "9px",
													userSelect: "none",
													borderRight: "1px solid #eaedf3",
													flexShrink: 0,
													cursor: "pointer",
													position: "relative",
												}}
												onClick={() => setCountryDropdownOpen((o) => !o)}
											>
												{selectedCountry.flag}
												<span
													style={{
														fontSize: "13.5px",
														fontWeight: "700",
														color: "#2d3035",
														whiteSpace: "nowrap",
													}}
												>
													{selectedCountry.label}
												</span>
												<svg
													width="8"
													height="5"
													viewBox="0 0 8 5"
													fill="none"
													style={{
														marginLeft: "2px",
														transform: countryDropdownOpen
															? "rotate(180deg)"
															: "rotate(0deg)",
														transition: "transform 0.2s",
													}}
												>
													<path
														d="M1 1L4 4L7 1"
														stroke="#4b5563"
														strokeWidth="1.5"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
												{countryDropdownOpen && (
													<div
														style={{
															position: "absolute",
															top: "calc(100% + 6px)",
															left: 0,
															background: "#fff",
															border: "1px solid #E5E7EB",
															borderRadius: "10px",
															boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
															zIndex: 1000,
															minWidth: "160px",
															padding: "6px 0",
															maxHeight: "200px",
															overflowY: "auto",
														}}
													>
														{COUNTRIES.map((c) => (
															<button
																key={c.code}
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	handleCountryChange(c);
																}}
																style={{
																	display: "flex",
																	alignItems: "center",
																	width: "100%",
																	padding: "8px 14px",
																	border: "none",
																	background:
																		selectedCountry.code === c.code
																			? "#f3f4f6"
																			: "#fff",
																	cursor: "pointer",
																	gap: "10px",
																	textAlign: "left",
																}}
															>
																{c.flag}
																<span
																	style={{
																		fontSize: "13px",
																		fontWeight: "600",
																		color: "#2d3035",
																		flex: 1,
																	}}
																>
																	{c.label}
																</span>
																{selectedCountry.code === c.code && (
																	<span
																		style={{
																			color: "#00b159",
																			fontWeight: "bold",
																			fontSize: "14px",
																		}}
																	>
																		✓
																	</span>
																)}
															</button>
														))}
													</div>
												)}
											</div>
											<input
												type="tel"
												placeholder={selectedCountry.placeholder}
												style={{
													border: "none",
													outline: "none",
													height: "100%",
													width: "100%",
													paddingLeft: "12px",
													paddingRight: "14px",
													fontSize: "14px",
													fontWeight: "600",
													color: "var(--zr-ink)",
													background: "transparent",
												}}
												value={phoneVal}
												onFocus={() => setIsFocused(true)}
												onBlur={() => {
													setIsFocused(false);
													setPhoneTouched(true);
												}}
												onChange={(e) => handlePhoneChange(e.target.value)}
											/>
										</div>
										{phoneError && (
											<span
												style={{
													fontSize: "12px",
													color: "#e53935",
													marginTop: "4px",
													display: "block",
												}}
											>
												{phoneError}
											</span>
										)}
									</div>
									<div className="zr-field">
										<span className="zr-field__label">Email de contacto</span>
										<input
											type="email"
											className="zr-input"
											value={barForm.contactEmail}
											onChange={(e) => setField("contactEmail", e.target.value)}
											placeholder="bar@ejemplo.com"
										/>
									</div>
									<div className="zr-field">
										<span className="zr-field__label">
											Sitio web (opcional)
										</span>
										<input
											type="url"
											className="zr-input"
											value={barForm.website}
											onChange={(e) => setField("website", e.target.value)}
											placeholder="https://www.tu-bar.com"
										/>
									</div>
									<div className="zr-field">
										<span className="zr-field__label">
											Instagram (opcional)
										</span>
										<input
											type="text"
											className="zr-input"
											value={barForm.instagram}
											onChange={(e) => setField("instagram", e.target.value)}
											placeholder="nordico.rooftop"
										/>
									</div>
								</Col>
							</Row>

							{/* Cover image */}
							<div className="zr-field" style={{ marginTop: 28 }}>
								<span className="zr-field__label">
									Imagen de portada{" "}
									<span
										style={{
											fontWeight: 400,
											color: "#6b7280",
											fontSize: "12px",
											marginLeft: "6px",
										}}
									>
										(Recomendado: 1600 x 900 px)
									</span>
								</span>
								<div
									className="zr-upload"
									style={{
										height: 220,
										cursor: "pointer",
										position: "relative",
										overflow: "hidden",
										border:
											dragTarget === "cover"
												? "2px dashed var(--zr-lime)"
												: undefined,
									}}
									onClick={() => fileInputRef.current?.click()}
									{...createDropHandlers("cover", (file) =>
										setField("imageFile", file),
									)}
								>
									<input
										ref={fileInputRef}
										type="file"
										accept="image/*"
										style={{ display: "none" }}
										onChange={handleImageChange}
									/>
									{barForm.imageFile || barForm.existingImage ? (
										<>
											<img
												src={
													barForm.imageFile
														? URL.createObjectURL(barForm.imageFile)
														: resolveUrl(barForm.existingImage)
												}
												alt="Portada"
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover",
													position: "absolute",
													inset: 0,
												}}
											/>
											<button
												type="button"
												aria-label="Quitar imagen"
												onClick={handleClearCover}
												className="zr-delete-img-btn zr-delete-img-btn--cover"
											>
												<svg
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
												</svg>
											</button>
										</>
									) : (
										<>
											<svg
												viewBox="0 0 24 24"
												width="28"
												height="24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.6"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
												<polyline points="17 8 12 3 7 8" />
												<line x1="12" y1="3" x2="12" y2="15" />
											</svg>
											<span className="t">Subir imagen de portada</span>
											<span className="h">
												Arrastrá o seleccioná un archivo JPG / PNG
											</span>
										</>
									)}
								</div>
							</div>

							{/* Gallery */}
							<div className="zr-field" style={{ marginTop: 24 }}>
								<span className="zr-field__label">
									Galería de fotos | 5 máx.{" "}
									<span
										style={{
											fontWeight: 400,
											color: "#6b7280",
											fontSize: "12px",
											marginLeft: "6px",
										}}
									>
										(Recomendado: 1080 x 1080 px)
									</span>
								</span>
								<div className="zr-gallery">
									{(barForm.existingGallery || []).map((url, idx) => (
										<div
											key={`existing-${idx}`}
											className="zr-gallery__slot"
											style={{ position: "relative", overflow: "hidden" }}
										>
											<img
												src={resolveUrl(url)}
												alt="Galería existente"
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover",
												}}
											/>
											<button
												type="button"
												aria-label="Quitar foto"
												onClick={() =>
													setBarForm((prev) => ({
														...prev,
														existingGallery: prev.existingGallery.filter(
															(_, i) => i !== idx,
														),
													}))
												}
												className="zr-delete-img-btn zr-delete-img-btn--gallery"
											>
												<svg
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
												</svg>
											</button>
										</div>
									))}
									{(barForm.galleryFiles || []).map((file, idx) => (
										<div
											key={`new-${idx}`}
											className="zr-gallery__slot"
											style={{ position: "relative", overflow: "hidden" }}
										>
											<img
												src={URL.createObjectURL(file)}
												alt="Nueva foto"
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover",
												}}
											/>
											<button
												type="button"
												aria-label="Quitar foto"
												onClick={() =>
													setBarForm((prev) => ({
														...prev,
														galleryFiles: prev.galleryFiles.filter(
															(_, i) => i !== idx,
														),
													}))
												}
												className="zr-delete-img-btn zr-delete-img-btn--gallery"
											>
												<svg
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
												</svg>
											</button>
										</div>
									))}
									{barForm.existingGallery.length +
										barForm.galleryFiles.length <
										5 && (
											<label className="zr-gallery__add">
												<svg
													width="14"
													height="14"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="1.8"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M12 5v14M5 12h14" />
												</svg>
												<span>Subir Foto</span>
												<input
													type="file"
													accept="image/*"
													multiple
													style={{ display: "none" }}
													onChange={(e) => {
														const files = Array.from(e.target.files || []);
														setBarForm((prev) => {
															const allowedCount =
																5 -
																(prev.existingGallery.length +
																	prev.galleryFiles.length);
															return {
																...prev,
																galleryFiles: [
																	...prev.galleryFiles,
																	...files.slice(0, allowedCount),
																],
															};
														});
													}}
												/>
											</label>
										)}
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Section 2: Ubicación */}
				{activeSection === 1 && (
					<section id="sec-ubicacion">
						<div className="zr-panel">
							<div className="zr-section-head">
								<h2 className="zr-section-head__title">Ubicación</h2>
								<p className="zr-section-head__hint">
									| Dónde está tu bar. Aparecerá en los filtros de búsqueda.
								</p>
							</div>
							<Row className="g-4">
								<Col md={6}>
									<div className="zr-field">
										<span className="zr-field__label">Provincia</span>
										<OrganizerPillSelect
											placeholder="Sin provincia"
											value={barForm.stateid}
											// Changing province invalidates the previously chosen city.
											onChange={(val) =>
												setBarForm((prev) => ({ ...prev, stateid: val, cityid: "" }))
											}
											options={[
												{ value: "", label: "Sin provincia" },
												...states.map((s) => ({ value: s._id, label: s.name })),
											]}
										/>
									</div>
								</Col>
								<Col md={6}>
									<div className="zr-field">
										<span className="zr-field__label">Ciudad</span>
										<OrganizerPillSelect
											placeholder="Sin ciudad"
											value={barForm.cityid}
											searchable
											disabled={!barForm.stateid}
											onChange={(val) => setField("cityid", val)}
											options={[
												{ value: "", label: "Sin ciudad" },
												...cities.map((c) => ({ value: c._id, label: c.name })),
											]}
										/>
									</div>
								</Col>
								<Col md={12}>
									<div className="zr-field">
										<span className="zr-field__label">Dirección</span>
										<ClubAddressAutocomplete
											value={barForm.address}
											// A manual edit invalidates the previous confirmed pin.
											onChange={(value) =>
												setBarForm((prev) =>
													applyManualBarAddressEdit(prev, value),
												)
											}
											onPlaceSelected={({ address, lat, lng }) =>
												setBarForm((prev) => ({ ...prev, address, lat, lng }))
											}
										/>
									</div>
								</Col>
							</Row>
							<div className="zr-field" style={{ marginTop: 24 }}>
								<span className="zr-field__label">Ubicación en el mapa</span>
								<span className="zr-field__hint">Buscamos la dirección automáticamente. Si el pin no cae en el lugar exacto, 
									arrastralo o hacé clic en el mapa para corregirlo.</span>
								<div className="zr-map">
									{(() => {
										const cityName =
											cities.find(
												(c) => String(c._id) === String(barForm.cityid),
											)?.name || "";
										const stateName =
											states.find(
												(s) => String(s._id) === String(barForm.stateid),
											)?.name || "";
										const queryAddress = [barForm.address, cityName, stateName]
											.filter(Boolean)
											.join(", ");
										return (
											<ClubLocationPicker
												address={queryAddress}
												lat={barForm.lat}
												lng={barForm.lng}
												entityLabel="bar"
												onChange={({ lat, lng }) =>
													setBarForm((prev) => ({ ...prev, lat, lng }))
												}
											/>
										);
									})()}
								</div>
							</div>
						</div>
					</section>
				)}

				{/* Section 3: Horarios */}
				{activeSection === 2 && (
					<section id="sec-horarios">
						<Row className="g-4 align-items-start">
							<Col lg={6}>
								<div className="zr-panel">
									<div className="zr-section-head">
										<h2 className="zr-section-head__title">
											Horarios de atención
										</h2>
										<p className="zr-section-head__hint">
											| Activa los días que abris y definí la franja horaria de
											cada uno.
										</p>
									</div>
									{barForm.openingHours.map((row) => {
										const day = WEEK_DAYS.find(
											(d) => d.weekday === row.weekday,
										);
										return (
											<div className="zr-day-row" key={row.weekday}>
												<span className="zr-day-row__name">
													{day?.label || row.weekday}
												</span>
												<input
													type="checkbox"
													className="zr-switch"
													checked={row.enabled}
													onChange={() =>
														setOpeningHour(row.weekday, "enabled", !row.enabled)
													}
												/>
												<OrganizerTimePillSelect
													value={row.open}
													onChange={(val) =>
														setOpeningHour(row.weekday, "open", val)
													}
													disabled={!row.enabled}
													quarterHours
													triggerClassName="zr-day-row__time"
												/>
												<OrganizerTimePillSelect
													value={row.close}
													onChange={(val) =>
														setOpeningHour(row.weekday, "close", val)
													}
													disabled={!row.enabled}
													quarterHours
													triggerClassName="zr-day-row__time"
												/>
											</div>
										);
									})}
								</div>
							</Col>
							<Col lg={6} className="d-flex flex-column gap-4">
								<div className="zr-panel">
									{(() => {
										const totalHours = barForm.openingHours
											.filter((h) => h.enabled)
											.reduce(
												(sum, h) => sum + calculateHours(h.open, h.close),
												0,
											);
										return (
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "baseline",
												}}
											>
												<h2 className="zr-section-head__title">
													Resúmen semanal
												</h2>
												<span
													className="zr-section-head__hint"
													style={{ margin: 0 }}
												>
													<b>{totalHours}</b> hs en total
												</span>
											</div>
										);
									})()}
									<div className="zr-week">
										{["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(
											(label, idx) => {
												const row = barForm.openingHours.find(
													(h) => h.weekday === idx,
												);
												const on = row?.enabled;
												const hrs = on
													? calculateHours(row.open, row.close)
													: 0;
												return (
													<div
														key={idx}
														className={
															"zr-week__day" + (on ? " zr-week__day--on" : "")
														}
													>
														<span>{label}</span>
														{on && (
															<span className="hrs">
																{hrs} hs
																<br />
																al día
															</span>
														)}
													</div>
												);
											},
										)}
									</div>
								</div>
								{renderFormFooter()}
							</Col>
						</Row>
					</section>
				)}

				{/* Section 4: Distribución */}
				{activeSection === 3 && (
					<section id="sec-distribucion">
						<div className="zr-panel">
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "flex-start",
									marginBottom: 8,
								}}
							>
								<div>
									<h2 className="zr-section-head__title">
										Distribución de mesas
									</h2>
									<p className="zr-section-head__hint">
										| Definí las zonas del bar y la cantidad de mesas en cada
										una.
									</p>
								</div>
								<div className="zr-totals">
									<span className="zr-total">
										Total de mesas{" "}
										<b>
											{String(
												barForm.zones.reduce(
													(s, z) => s + Number(z.tableCount || 0),
													0,
												),
											).padStart(2, "0")}
										</b>
									</span>
									<span className="zr-total">
										Total de asientos{" "}
										<b>
											{String(
												barForm.zones.reduce(
													(s, z) =>
														s +
														Number(z.tableCount || 0) *
														Number(z.tableCapacity || 4),
													0,
												),
											).padStart(2, "0")}
										</b>
									</span>
								</div>
							</div>
							<span
								className="zr-field__label"
								style={{ display: "block", margin: "14px 0 8px" }}
							>
								Zona
							</span>
							{barForm.zones.map((zona, idx) => (
								<div className="zr-zone-row" key={idx}>
									<div className="zr-zone-row__name">
										<input
											className="zr-input"
											placeholder="Ej: Interior, Vereda, VIP"
											value={zona.name}
											onChange={(e) =>
												setBarForm((prev) => ({
													...prev,
													zones: prev.zones.map((z, i) =>
														i === idx ? { ...z, name: e.target.value } : z,
													),
												}))
											}
										/>
									</div>
									<div className="zr-zone-row__qty">
										<div className="zr-inline">
											<input
												type="number"
												min={1}
												max={50}
												value={zona.tableCount || ""}
												onChange={(e) =>
													setBarForm((prev) => ({
														...prev,
														zones: prev.zones.map((z, i) =>
															i === idx
																? {
																	...z,
																	tableCount:
																		e.target.value === ""
																			? ""
																			: Number(e.target.value),
																}
																: z,
														),
													}))
												}
											/>
											<span>mesas</span>
										</div>
									</div>
									<div className="zr-zone-row__seats">
										<div className="zr-inline">
											<input
												type="number"
												min={1}
												max={20}
												value={zona.tableCapacity || ""}
												onChange={(e) =>
													setBarForm((prev) => ({
														...prev,
														zones: prev.zones.map((z, i) =>
															i === idx
																? {
																	...z,
																	tableCapacity:
																		e.target.value === ""
																			? ""
																			: Number(e.target.value),
																}
																: z,
														),
													}))
												}
											/>
											<span>personas por mesa</span>
										</div>
									</div>
									{barForm.zones.length > 1 ? (
										<button
											type="button"
											className="zr-delete-btn"
											aria-label="Eliminar zona"
											onClick={() =>
												setBarForm((prev) => ({
													...prev,
													zones: prev.zones.filter((_, i) => i !== idx),
												}))
											}
										>
											<svg
												width="18"
												height="20"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
											</svg>
										</button>
									) : (
										<div />
									)}
								</div>
							))}
							<button
								type="button"
								className="zr-add-zone"
								onClick={() =>
									setBarForm((prev) => ({
										...prev,
										zones: [
											...prev.zones,
											{
												name: "",
												tableCount: 2,
												tableCapacity: 4,
												price: 0,
												priceEnabled: false,
											},
										],
									}))
								}
							>
								<svg
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.8"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M12 5v14M5 12h14" />
								</svg>
								Agregar zona
							</button>
						</div>
					</section>
				)}

				{/* Section 5: Políticas */}
				{activeSection === 4 && (
					<section id="sec-politicas">
						<div className="zr-panel">
							<div className="zr-section-head">
								<h2 className="zr-section-head__title">Políticas de reserva</h2>
								<p className="zr-section-head__hint">
									| Reglas que se aplican a todas las reservas de este bar.
								</p>
							</div>
							<OnlineBookingSwitch
								checked={barForm.onlineBookingEnabled}
								onChange={(value) => setField("onlineBookingEnabled", value)}
								disabled={saving}
							/>
							<Row className="g-4">
								<Col md={6} className="d-flex flex-column gap-4">
									<div className="zr-field">
										<span className="zr-field__label">
											Tiempo de antelación
										</span>
										<OrganizerPillSelect
											value={barForm.reservationWindowDays}
											onChange={(val) =>
												setField("reservationWindowDays", Number(val))
											}
											options={reservationWindowOptions}
										/>
									</div>
									<div className="zr-field">
										<span className="zr-field__label">
											Tiempo entre reservas (por mesa)
										</span>
										<OrganizerPillSelect
											value={barForm.slotDurationMinutes}
											onChange={(val) =>
												setField("slotDurationMinutes", Number(val))
											}
											options={SLOT_DURATION_OPTIONS}
										/>
									</div>
									<div className="zr-field">
										<span className="zr-field__label">
											Tolerancia por llegada tarde
										</span>
										<OrganizerPillSelect
											value={barForm.toleranceMinutes}
											onChange={(val) =>
												setField("toleranceMinutes", Number(val))
											}
											options={toleranceOptions}
										/>
									</div>
									{barForm.cancellationAllowed && (
										<div className="zr-field">
											<span className="zr-field__label">
												Plazo de cancelación sin penalidad
											</span>
											<OrganizerPillSelect
												value={barForm.cancellationPolicyHours}
												onChange={(val) =>
													setField("cancellationPolicyHours", Number(val))
												}
												options={CANCELLATION_HOURS_OPTIONS}
											/>
										</div>
									)}
								</Col>
								<Col md={6} className="zr-politicas-right">
									<div className="zr-row-toggle">
										<div className="zr-row-toggle__txt">
											<span className="t">Cancelación de reserva</span>
											<div className="h">
												| Configurar permiso de cancelación
											</div>
										</div>
										<input
											type="checkbox"
											className="zr-switch"
											checked={barForm.cancellationAllowed}
											onChange={() =>
												setField(
													"cancellationAllowed",
													!barForm.cancellationAllowed,
												)
											}
										/>
									</div>
									<div className="zr-field zr-field--textarea">
										<span className="zr-field__label">
											Notas adicionales para el cliente (opcional)
										</span>
										<textarea
											className="zr-textarea"
											value={barForm.additionalNotes}
											onChange={(e) =>
												setField("additionalNotes", e.target.value)
											}
											placeholder="Ej: Se requiere confirmación de llegada, traer documento..."
										/>
										<p className="zr-section-head__hint">
											* Estas notas se muestran al usuario en el paso de
											confirmación.
										</p>
									</div>
								</Col>
								<Col md={12}>
									<RecordatoriosAjustes
										activos={barForm.recordatoriosActivos}
										horasAntes={barForm.horasAntesRecordatorio}
										onChange={setField}
										whatsapp={barForm.whatsapp}
										onWhatsappChange={(whatsapp) => setField("whatsapp", whatsapp)}
										push={barForm.push}
										onPushChange={(push) => setField("push", push)}
										local={barForm.name}
										errorWhatsapp={waError}
										onAntesDeIrAChat={borrador.guardar}
										volver={borrador.volver}
									/>
								</Col>
								<Col md={12}>
									<PedidoResenaAjustes
										resena={barForm.resena}
										onChange={(resena) => setField("resena", resena)}
										local={barForm.name}
										errorWhatsapp={resenaError}
										onAntesDeIrAChat={borrador.guardar}
										volver={borrador.volver}
									/>
								</Col>
								<Col md={12}>
									<ListaEsperaAjustes
										lista={barForm.listaEspera}
										onChange={(listaEspera) => setField("listaEspera", listaEspera)}
										local={barForm.name}
										errorWhatsapp={listaEsperaError}
										onAntesDeIrAChat={borrador.guardar}
										volver={borrador.volver}
									/>
								</Col>
							</Row>
						</div>
					</section>
				)}

				{/* Section 6: Pago */}
				{activeSection === 5 && (
					<section id="sec-pago">
						<div className="zr-panel">
							<div className="zr-section-head" style={{ marginBottom: 18 }}>
								<h2 className="zr-section-head__title">Pago de reserva</h2>
								<p className="zr-section-head__hint">
									| Cómo y cuándo cobran sus mesas.
								</p>
							</div>
							{senasBloqueadas && <AvisoSenasBloqueadas />}
							<Row className="g-4 align-items-start">
								<Col md={6}>
									<div className="zr-row-toggle">
										<div className="zr-row-toggle__txt">
											<span className="t">Cobro anticipado</span>
											<div className="h">
												| Define si el cliente pagará una seña al momento de confirmar su
												reserva
											</div>
										</div>
										<input
											type="checkbox"
											className="zr-switch"
											checked={cobraSenas}
											disabled={senasBloqueadas}
											onChange={() =>
												setField(
													"reservationMode",
													barForm.reservationMode === "deposit"
														? "free"
														: "deposit",
												)
											}
										/>
									</div>
								</Col>
								{cobraSenas && (
									<Col md={6}>
										<div className="zr-field">
											<span className="zr-field__label">
												Monto de seña general (ARS)
											</span>
											<input
												type="number"
												min={0}
												className="zr-input"
												value={barForm.depositAmount || ""}
												onChange={(e) =>
													setField(
														"depositAmount",
														e.target.value === "" ? "" : Number(e.target.value),
													)
												}
												placeholder="0"
											/>
										</div>
									</Col>
								)}
							</Row>
							{cobraSenas &&
								barForm.zones.length > 0 && (
									<>
										<div className="zr-check-row" style={{ marginTop: 22 }}>
											<input
												type="checkbox"
												className="zr-check"
												checked={Boolean(barForm.zonePricingEnabled)}
												onChange={() =>
													setField(
														"zonePricingEnabled",
														!barForm.zonePricingEnabled,
													)
												}
											/>
											Precio de seña por zona
										</div>
										<p className="zr-section-head__hint">
											| Las zonas sin precio propio usan el monto general de
											seña establecido.
										</p>
										{barForm.zonePricingEnabled && (
											<Row className="g-3" style={{ marginTop: 6 }}>
												{barForm.zones.map((zona, idx) => (
													<Col md={3} key={idx}>
														<div className="zr-field">
															<span className="zr-field__label">
																{zona.name || `Zona ${idx + 1}`}
															</span>
															<input
																type="number"
																min={0}
																className="zr-input"
																value={zona.price || ""}
																onChange={(e) =>
																	setBarForm((prev) => ({
																		...prev,
																		zones: prev.zones.map((z, i) =>
																			i === idx
																				? {
																					...z,
																					price:
																						e.target.value === ""
																							? ""
																							: Number(e.target.value),
																				}
																				: z,
																		),
																	}))
																}
																placeholder="0"
															/>
														</div>
													</Col>
												))}
											</Row>
										)}
									</>
								)}
							<div className="zr-notice" style={{ marginTop: 22 }}>
								<svg
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="currentColor"
								>
									<path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 5.5a1.5 1.5 0 0 1 3 0v6a1.5 1.5 0 0 1-3 0zM12 18.4a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z" />
								</svg>
								Zoco no cobra cargos por servicio ni por reserva. El precio que
								definas es el que pagará tu cliente.
							</div>
						</div>
					</section>
				)}
			</div>

			{activeSection !== 2 && renderFormFooter()}
		</div>
	);
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const ReservasOrganizer = () => {
	const { setPageHeader } = useContext(OrganizerHeaderContext);

	useEffect(() => {
		setPageHeader({
			title: "Reservas de mesas",
			subtitle: "Gestión de bares, mesas, horarios y reservas.",
		});
	}, [setPageHeader]);

	// Hasta saber si cobra, las señas quedan bloqueadas para no guardar a mitad de camino.
	const { cargando: cargandoCobros, puedeCobrar } = useCobrosHabilitados();
	const senasBloqueadas = cargandoCobros || !puedeCobrar;

	/* ── routing ── */
	const [route, setRoute] = useState("list"); // "list" | "form" | "reservations"
	const [selectedBar, setSelectedBar] = useState(null);
	// Guided first run: shown while the negocio has no bar (1 bar per negocio; a new one means a new negocio).
	const [guiado, setGuiado] = useState(false);
	const multiSede = useMultiSede();
	const [seccionInicial, setSeccionInicial] = useState(0);

	/* ── data ── */
	const [bars, setBars] = useState([]);
	const [states, setStates] = useState([]);

	/* ── form state ── */
	const [barForm, setBarForm] = useState(emptyBarForm());

	/* ── ui state ── */
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [pageError, setPageError] = useState("");
	const [formError, setFormError] = useState("");
	const [toastMessage, setToastMessage] = useState("");
	const [confirmBar, setConfirmBar] = useState(null); // bar object to delete

	/* ── load catalog ── */
	const loadLocationCatalog = useCallback(async () => {
		try {
			const statesRes = await getState();
			setStates(
				Array.isArray(statesRes?.data?.info) ? statesRes.data.info : [],
			);
		} catch (_) { }
	}, []);

	/* ── load bars ── */
	const loadBars = useCallback(async () => {
		const response = await organizerListBarsApi();
		const list = Array.isArray(response?.data?.info) ? response.data.info : [];
		setBars(list);
		return list;
	}, []);

	/* ── initial load ── */
	useEffect(() => {
		(async () => {
			try {
				setLoading(true);
				setPageError("");
				const [list] = await Promise.all([loadBars(), loadLocationCatalog()]);
				setGuiado(list.length === 0);
			} catch (err) {
				setPageError(
					err?.response?.data?.message ||
					"No pudimos cargar el módulo de reservas.",
				);
			} finally {
				setLoading(false);
			}
		})();
	}, [loadBars, loadLocationCatalog]);

	/* ── navigate to form for an existing bar ── */
	const handleEdit = async (barId, sectionId) => {
		try {
			setLoading(true);
			setFormError("");
			const res = await organizerGetBarApi(barId);
			const info = res?.data?.info || {};
			setBarForm(normalizeBarForForm(info));
			setSeccionInicial(Math.max(0, SECTION_IDS.indexOf(sectionId)));
			setRoute("form");
		} catch (err) {
			handleError(err);
		} finally {
			setLoading(false);
		}
	};

	// Coming back from Zoco Chat (templates): reopen the bar's form on Políticas.
	useVolverAPoliticas({ listo: !loading, locales: bars, abrir: (bar) => handleEdit(bar._id, "politicas") });

	/* ── open delete confirm ── */
	const handleDeleteRequest = (bar) => {
		setConfirmBar(bar);
	};

	/* ── confirm delete ── */
	const handleDeleteConfirm = async () => {
		if (!confirmBar) return;
		try {
			setSaving(true);
			await organizerDeleteBarApi(confirmBar._id);
			await loadBars();
			setToastMessage(`"${confirmBar.name}" eliminado correctamente.`);
		} catch (err) {
			handleError(err);
		} finally {
			setSaving(false);
			setConfirmBar(null);
		}
	};

	/* ── toggle active/inactive ── */
	const handleToggleStatus = async (bar) => {
		try {
			setSaving(true);
			const res = await organizerToggleBarApi(bar._id);
			const isEnabled = res?.data?.info?.isEnabled;
			await loadBars();
			setToastMessage(
				isEnabled === false
					? `"${bar.name}" desactivado.`
					: `"${bar.name}" activado.`,
			);
		} catch (err) {
			handleError(err);
		} finally {
			setSaving(false);
		}
	};

	/* ── save bar ── */
	const handleSave = async () => {
		setFormError("");

		if (!barForm.name.trim()) {
			setFormError("El nombre del bar es obligatorio.");
			return;
		}
		if (!barForm.contactPhone?.trim()) {
			setFormError("El teléfono de contacto es obligatorio.");
			return;
		}
		const phone = barForm.contactPhone || "";
		let matchedCountry = null;
		let matchedNumber = phone.trim();
		for (const c of COUNTRIES) {
			if (phone.startsWith(c.prefix)) {
				matchedCountry = c;
				matchedNumber = phone.slice(c.prefix.length).trim();
				break;
			}
		}
		if (!matchedCountry || !matchedCountry.validate(matchedNumber)) {
			setFormError("El número de celular no es válido.");
			return;
		}
		if (
			barForm.contactEmail?.trim() &&
			!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(barForm.contactEmail.trim())
		) {
			setFormError("El email de contacto no tiene un formato válido.");
			return;
		}
		if (
			barForm.website?.trim() &&
			!/^https?:\/\/.+\..+/.test(barForm.website.trim())
		) {
			setFormError("El sitio web debe comenzar con http:// o https://");
			return;
		}
		if (
			barForm.instagram?.trim() &&
			!/^[a-zA-Z0-9_.]{1,30}$/.test(barForm.instagram.trim().replace(/^@/, ""))
		) {
			setFormError(
				"El usuario de Instagram solo puede contener letras, números, puntos y guiones bajos (sin @).",
			);
			return;
		}
		if (!barForm.zones.length) {
			setFormError("Agregá al menos una zona con nombre.");
			return;
		}
		for (let i = 0; i < barForm.zones.length; i++) {
			const z = barForm.zones[i];
			if (!z.name?.trim()) {
				setFormError(`La zona ${i + 1} no tiene nombre.`);
				return;
			}
			if (!Number(z.tableCount) || Number(z.tableCount) < 1) {
				setFormError(`La zona "${z.name}" debe tener al menos 1 mesa.`);
				return;
			}
			if (!Number(z.tableCapacity) || Number(z.tableCapacity) < 1) {
				setFormError(
					`La zona "${z.name}" debe tener al menos 1 persona por mesa.`,
				);
				return;
			}
		}
		if (!(barForm.openingHours || []).some((h) => h.enabled)) {
			setFormError(
				"Habilitá al menos un día de atención en la sección Horarios.",
			);
			return;
		}
		// Solo se fuerza gratis con el bloqueo confirmado; mientras carga no se pisa nada.
		const formAGuardar =
			!cargandoCobros && !puedeCobrar ? sinSenasEnBar(barForm) : barForm;
		if (
			formAGuardar.reservationMode === "deposit" &&
			(!formAGuardar.depositAmount || Number(formAGuardar.depositAmount) <= 0)
		) {
			setFormError("Ingresá un monto de seña mayor a $0.");
			return;
		}
		try {
			setSaving(true);
			const formData = buildBarFormData(formAGuardar);

			if (barForm.barId) {
				await organizerUpdateBarApi(formData);
			} else {
				await organizerCreateBarApi(formData);
			}

			borrarBorrador(barForm.barId);
			await loadBars();
			setToastMessage(
				barForm.barId
					? "Bar actualizado correctamente."
					: "Bar creado correctamente.",
			);
			setFormError("");
			setRoute("list");
		} catch (err) {
			setFormError(getCleanErrorMessage(err));
			handleError(err);
		} finally {
			setSaving(false);
		}
	};

	/* ── guided first run finished: back to the module, or straight to the form section left pending ── */
	const handleTerminarGuiado = async (ctx, sectionId) => {
		setGuiado(false);
		try {
			await loadBars();
		} catch (err) {
			handleError(err);
		}
		if (ctx?.id && sectionId) await handleEdit(ctx.id, sectionId);
	};

	/* ── initial loading ── */
	if (loading && !bars.length && !pageError) return <SpinLoading />;

	return (
		<div className="zr-app zr-main">
			{pageError && (
				<div className="org-page-error">
					<p>{pageError}</p>
					<button type="button" onClick={() => window.location.reload()}>
						Reintentar
					</button>
				</div>
			)}

			{route === "list" && guiado && !loading && (
				<ReservasGuidedFlow
					states={states}
					onTerminar={handleTerminarGuiado}
					onCancelar={async () => {
						setGuiado(false);
						// Un bar ya creado en el flujo tiene que aparecer en la lista al volver.
						try {
							await loadBars();
						} catch (err) {
							handleError(err);
						}
					}}
				/>
			)}

			{route === "list" && !guiado && (
				<OrgListScreen
					bars={bars}
					states={states}
					loading={loading}
					multiSede={multiSede}
					onEmpezar={() => setGuiado(true)}
					onEdit={handleEdit}
					onDelete={handleDeleteRequest}
					onToggleStatus={handleToggleStatus}
					onShowReservations={(bar) => {
						setSelectedBar(bar);
						setRoute("reservations");
					}}
					onCopyPublicLink={async (bar) => {
						const url = `${window.location.origin}/reservas/${bar._id}`;
						try {
							if (navigator?.clipboard?.writeText) {
								await navigator.clipboard.writeText(url);
							} else {
								const textArea = document.createElement("textarea");
								textArea.value = url;
								document.body.appendChild(textArea);
								textArea.select();
								document.execCommand("copy");
								document.body.removeChild(textArea);
							}
							setToastMessage("Enlace copiado al portapapeles.");
						} catch (err) {
							setToastMessage("No se pudo copiar el enlace.");
						}
					}}
				/>
			)}

			{route === "form" && (
				<OrgFormScreen
					barForm={barForm}
					setBarForm={setBarForm}
					states={states}
					saving={saving}
					onSave={handleSave}
					onCancel={() => {
						setRoute("list");
						setFormError("");
					}}
					formError={formError}
					senasBloqueadas={senasBloqueadas}
					initialSection={seccionInicial}
				/>
			)}

			{route === "reservations" && selectedBar && (
				<OrgReservationsScreen
					bar={selectedBar}
					onBack={() => {
						setRoute("list");
						setSelectedBar(null);
					}}
					setToastMessage={setToastMessage}
				/>
			)}

			{/* Confirm delete modal */}
			{confirmBar && (
				<ConfirmModal
					barName={confirmBar.name}
					onConfirm={handleDeleteConfirm}
					onCancel={() => setConfirmBar(null)}
				/>
			)}

			{/* Toast */}
			<OrgToast message={toastMessage} onClose={() => setToastMessage("")} />
		</div>
	);
};

export default ReservasOrganizer;
